import { createApi } from '@reduxjs/toolkit/query/react';

import { getBaseUrlObNextGen } from '../../../constants/obNextGenConstants';
import { axiosBaseQuery } from '../../../services/baseQuery';
import type {
  FetchCategoriesTemplateRequest,
  FetchCategoriesTemplateResponse,
  Group,
} from '../data-layer/types';

// ----- Shared Types -----
interface FetchApiDataArgs {
  apiKey: string;
  endpoint: string;
  offset?: number;
  max?: number;
  findBy?: string;
}

interface ValidateApplicationNameResponse {
  isValid: boolean;
}

interface SaveUseCaseConfigurationArgs {
  applicationtype: string;
  endpointKey: number;
  body: unknown;
}

type InitiateOnboardPayload = Record<string, unknown>;

interface TestConnectionArgs {
  endpointName: string;
}

interface OnboardingStepsCategoryResponse {
  Configuration: {
    Name: string;
    order: number;
  }[];
  parentCategoryOrder: number;
}

// ----- API Definition -----
export const disconnectedOnboardingApi = createApi({
  reducerPath: 'disconnectedOnboardingApi',
  baseQuery: axiosBaseQuery({ baseUrl: getBaseUrlObNextGen() }),
  endpoints: (builder) => ({
    getOnboardingStepsCategory: builder.query<
      OnboardingStepsCategoryResponse,
      void
    >({
      query: () => ({
        url: '/idw/admin/applications/categories/apptype/CUA',
        method: 'GET',
      }),
    }),

    fetchApiData: builder.query<unknown, FetchApiDataArgs>({
      query: ({ apiKey, endpoint, offset = 0, max = 25, findBy = '' }) => ({
        url: `${apiKey}${endpoint}`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: { offset, max, findBy },
      }),
      keepUnusedDataFor: 300,
    }),

    validateApplicationName: builder.query<
      ValidateApplicationNameResponse,
      string
    >({
      query: (applicationTypeName) => ({
        url: `/idw/admin/applications/validateapplicationname?applicationName=${applicationTypeName}`,
        method: 'GET',
      }),
    }),

    fetchAttributesData: builder.query<
      Group[],
      { apiURL: string; domainObjects: string[] }
    >({
      query: ({ apiURL, domainObjects }) => ({
        url: apiURL.replace('/api', ''),
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: domainObjects,
      }),
    }),

    saveUseCaseConfiguration: builder.mutation<
      unknown,
      SaveUseCaseConfigurationArgs
    >({
      query: ({ applicationtype, endpointKey, body }) => ({
        url: `/idw/admin/applications/${applicationtype}/${endpointKey}`,
        method: 'PUT',
        data: body,
      }),
    }),

    initiateOnboard: builder.mutation<unknown, InitiateOnboardPayload>({
      query: (payload) => ({
        url: '/idw/admin/applications/initiateonboard',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: payload,
      }),
    }),

    testConnection: builder.mutation<unknown, TestConnectionArgs>({
      query: ({ endpointName }) => ({
        url: '/idw/admin/applications/testconnection',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: {
          endpointName,
          connectiontype: 'REST',
          isOnBoarded: true,
          statusForEnableDisable: '1',
        },
      }),
    }),
  }),
});

// ----- Second API -----
export const fetchCategoriesTemplateApi = createApi({
  reducerPath: 'fetchCategoriesTemplateApi',
  baseQuery: axiosBaseQuery({ baseUrl: getBaseUrlObNextGen() }),
  endpoints: (builder) => ({
    fetchCategoriesTemplate: builder.query<
      FetchCategoriesTemplateResponse,
      FetchCategoriesTemplateRequest
    >({
      query: ({
        parentCategoryName,
        categoryNames,
        applicationType,
        endpointKey,
      }) => ({
        url: '/idw/admin/applications/v2/generatecategorytemplate',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: {
          parentCategoryName,
          categoryNames,
          applicationType,
          techOnBoard: true,
          ...(endpointKey && { endpointKey }),
        },
      }),
      keepUnusedDataFor: 600,
    }),
  }),
});

// ----- Hooks -----
export const {
  useGetOnboardingStepsCategoryQuery,
  useFetchApiDataQuery,
  useLazyFetchApiDataQuery,
  useLazyValidateApplicationNameQuery,
  useFetchAttributesDataQuery,
  useSaveUseCaseConfigurationMutation,
  useInitiateOnboardMutation,
  useTestConnectionMutation,
} = disconnectedOnboardingApi;

export const { useFetchCategoriesTemplateQuery } = fetchCategoriesTemplateApi;
